import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { catalogSnapshot, type Product } from "../../shared/products.ts";
import { listProducts } from "./api.ts";

// The catalog renders instantly from the build-time snapshot (so pre-rendered
// pages and first paint are never empty), then overlays the live D1 catalog
// from GET /api/products. `live` flips true once the API answered — consumers
// that must not act on stale data (cart cleanup, PDP 404) wait for it.

type ProductsContextValue = {
  products: Product[];
  live: boolean;
};

const ProductsContext = createContext<ProductsContextValue>({
  products: catalogSnapshot,
  live: false,
});

export function ProductsProvider({ children }: { children: ReactNode }) {
  const [products, setProducts] = useState<Product[]>(catalogSnapshot);
  const [live, setLive] = useState(false);

  useEffect(() => {
    let alive = true;
    listProducts()
      .then((fresh) => {
        if (!alive) return;
        setProducts(fresh);
        setLive(true);
      })
      .catch(() => {
        // API unreachable — keep serving the snapshot.
      });
    return () => {
      alive = false;
    };
  }, []);

  const value = useMemo(() => ({ products, live }), [products, live]);
  return (
    <ProductsContext.Provider value={value}>
      {children}
    </ProductsContext.Provider>
  );
}

export function useProducts(): ProductsContextValue {
  return useContext(ProductsContext);
}

export function useFeaturedProducts(): Product[] {
  const { products } = useProducts();
  return useMemo(
    () =>
      products
        .filter((p) => p.featuredOrder != null)
        .sort((a, b) => (a.featuredOrder ?? 0) - (b.featuredOrder ?? 0))
        .slice(0, 3),
    [products],
  );
}
